import { FC, useCallback } from "react";

import { PageLink } from "./PageLink";
import { translatePagePointReport } from "./utils";

import { serverGetPagePointReport } from "api/api-server-assessment";
import { PagePointTableData } from "api/api-types-assessment.ts";
import { ScrollableTableContainer } from "components/tables/ScrollableTableContainer";
import { TD, TH, THead, TR, Table } from "components/tables/TableElements";
import { t } from "i18n/i18n";
import { AssessmentCategoryKey } from "i18n/i18next";
import { Tooltip } from "mds/components/Tooltip";
import { useAppSelector } from "store/index";
import { pageSelectors, selectCurrentSectionId } from "store/selectors";

type PagePointTableProps = {
  pagePointTableData: PagePointTableData | null;
};

export const PagePointTable: FC<PagePointTableProps> = ({ pagePointTableData }) => {
  const pageEntities = useAppSelector(pageSelectors.selectEntities);
  const sectionId = useAppSelector(selectCurrentSectionId);

  const exportData = useCallback(async () => {
    if (!sectionId) return { headers: [], rows: [] };
    const reportData = await serverGetPagePointReport({
      section_id: sectionId,
    });
    return translatePagePointReport(reportData);
  }, [sectionId]);

  if (!pagePointTableData || pagePointTableData.rows.length === 0) {
    return null;
  }

  const { headers, rows } = pagePointTableData;

  return (
    <ScrollableTableContainer
      exportData={exportData}
      title={t("assessment_dashboard.student_points_per_page_title")}
    >
      <Table>
        {/* Headers: Page / Page Type / Avg. Points / User A / User B / User C... */}
        {/* One row per page, showing each student's score on that page */}
        <THead>
          <TR className="border-black-tint-70">
            <TH className="w-4/12 pl-4">{t("assessment_dashboard.all.page_header_title")}</TH>

            <TH className="w-16">
              <Tooltip text={t("assessment_dashboard.all.assessment_category_header_tooltip")}>
                {t("assessment_dashboard.all.assessment_category_header_title")}
              </Tooltip>
            </TH>

            <TH className="w-16">
              <Tooltip text={t("assessment_dashboard.all.avg_points_header_tooltip")}>
                {t("assessment_dashboard.all.avg_points_header_title")}
              </Tooltip>
            </TH>

            {headers.map((header) => (
              <TH key={header.id} verticalDivTitle={header.name} isVertical>
                {header.name}
              </TH>
            ))}
          </TR>
        </THead>

        <tbody>
          {rows.map((row) => (
            <TR key={row.page_id}>
              <TD className="max-w-40 truncate py-2.5 pl-[17px]" title={row.page_title}>
                <PageLink
                  className="w-full truncate text-blue-shade-20 underline"
                  page={pageEntities[row.page_id]}
                />
              </TD>
              <TD>
                {t(`assessment_categories.${row.assessment_category as AssessmentCategoryKey}`)}
              </TD>
              <TD>{row.avg_points}</TD>
              {row.student_scores.map((score, index) => (
                <TD key={headers[index].id}>{score}</TD>
              ))}
            </TR>
          ))}
        </tbody>
      </Table>
    </ScrollableTableContainer>
  );
};
